/**
 * Tier entitlements — the single place that answers "does this practice's plan include X?".
 *
 * This is deliberately shaped like the authorization chokepoint (`decide()`/`authorize()`),
 * for the same reason: a second, "equivalent" inline `tier === 'enterprise'` check written in
 * another handler is a second place the two silently drift apart. One pure function, one
 * closed feature union, exhaustively tested.
 *
 * It lives in `shared/` so the web app can hide or disable a feature using the *identical*
 * function the server enforces with. The UI can then never show "available" for something the
 * server would refuse.
 *
 * Entitlement is not authorization. Authorization asks "may this person do this?" and fails
 * with 403. Entitlement asks "does this plan include this?" and fails with a distinct
 * upgrade-shaped error. A user who lacks a capability by role must never be told to upgrade.
 *
 * See `docs/decisions/tiers-and-entitlements.md` and
 * `emr-po/docs/research/tier-gating-certified-capabilities.md`.
 */

/**
 * The three per-provider tiers, settled by `emr-po` A9 (2026-08-17). Exact price points and the
 * Growth-vs-Enterprise boundary remain refinable; the shape is not.
 *
 * Renaming these was a mechanical change precisely because every check goes through `isEntitled` —
 * which is the payoff for routing everything through one function rather than comparing tier strings
 * at call sites.
 */
export const TIERS = ['starter', 'growth', 'enterprise'] as const;
export type Tier = (typeof TIERS)[number];

/**
 * Commercial features. Gating these is ordinary product packaging and is unconstrained by the
 * certification and information-blocking rules — with two caveats from the C10 research that are
 * design rules rather than code:
 *
 *   1. None of these may be the *only practical path* for a customer or app to reach EHI or the
 *      certified API. "Interoperability element" (45 CFR §171.102) is defined broadly enough that
 *      gating the sole route to EHI is gating an interoperability element, whatever it's called.
 *   2. None may be bundled into the same SKU as certified-API access, which would convert a lawful
 *      commercial fee into a prohibited API fee under §170.404's catch-all.
 *
 * `dermatologyModule` (`BACKLOG.md` M3) is the first *specialty* module gated this way: optional,
 * invisible/opt-in by default, licensed like any other commercial add-on rather than folded into the
 * certified module above — a specialty content pack is ordinary product packaging, not an
 * interoperability element, so neither caveat above applies to it. `aestheticsModule`
 * (`BACKLOG.md` M9) is the second, gated identically for the identical reason.
 * `physicalTherapyModule` (`BACKLOG.md` M6), `pediatricsModule` (`BACKLOG.md` M8),
 * `gastroenterologyModule` (`BACKLOG.md` M5), and `orthopedicsModule` (`BACKLOG.md` M7) are the
 * third through sixth. `orthopedicsModule`'s own module content (condition templates, exam
 * maneuvers, PRO score storage, global-surgical-period billing, UDI capture) is ordinary commercial
 * content; UDI capture's status as an ONC certification criterion (§170.315(a)(14)) governs
 * *whether the certified module offers it at all*, not whether this specialty pack may be licensed
 * separately — the same reasoning that already applies to every other commercial feature in this
 * list. `ophthalmologyModule` (`BACKLOG.md` M10) is the seventh, gated identically — structured
 * refraction, glasses/contact-lens Rx, imaging-report ingestion, and cataract surgical planning are
 * all ordinary specialty content, not an interoperability element. `obgynModule` (`BACKLOG.md` M11)
 * is the eighth, gated identically for the identical reason — antepartum flowsheets, gestational-age
 * dating, ultrasound biometry, screening-order tracking, and EPDS scoring are all ordinary specialty
 * clinical content, not a certified interoperability element.
 */
export const COMMERCIAL_FEATURES = [
  'sso',
  'scim',
  'whiteLabel',
  'analytics',
  'dermatologyModule',
  'aestheticsModule',
  'physicalTherapyModule',
  'pediatricsModule',
  'gastroenterologyModule',
  'orthopedicsModule',
  'ophthalmologyModule',
  'obgynModule',
] as const;

/**
 * Capabilities that must NEVER be tier-gated, as a matter of regulation rather than product
 * choice. `isEntitled` returns true for these on every tier, including an unrecognised one.
 *
 *   - `onc.patientAccess` — patients are "API Users" under §170.404(c), and the only fee
 *     permitted to an API User is for optional value-added services. Independently,
 *     §171.302(b)(2) removes from the Fees Exception any fee based *in any part* on an
 *     individual's electronic access to their own EHI.
 *   - `onc.appDeveloperAccess` — third-party app developers are API Users on the same footing.
 *   - `onc.ehiExport` — §171.302(b)(3) excludes from the Fees Exception any fee to perform an EHI
 *     export via §170.315(b)(10) for the purposes of switching health IT or giving patients their
 *     EHI. Free, every tier, on request.
 *
 * These are settled, not pending. Do not move one into a tier table.
 */
export const NEVER_GATED = [
  'onc.patientAccess',
  'onc.appDeveloperAccess',
  'onc.ehiExport',
] as const;

/**
 * The provider-facing certified capabilities, licensed as one indivisible module.
 *
 * Charging provider customers for the certified API *would be* permitted
 * (§170.404(a)(3)(ii)-(iii)), but only as cost recovery on objective, verifiable, uniformly-applied
 * criteria — never as a value-based upsell, since "the enterprise tier unlocks FHIR" is not
 * defensible as cost recovery. `emr-po` A9 settles this by not charging differentially at all: every
 * paid tier licenses the whole module, so there is no differential to justify.
 *
 * What code *must* enforce is §170.402(a)(3) and ONC's Assurances guidance, which name "failing to
 * fully deploy or enable certified capabilities" and "imposing limitations on the use of certified
 * capabilities once deployed" as violating practices. So this is modelled as a single licensed
 * module rather than a set of independently-gateable features: a customer who licenses it gets the
 * entire certified scope, with no per-capability flags and no support ticket needed to switch
 * anything on. `certifiedCapabilitiesFor` below is the only sanctioned way to ask.
 *
 * Also note §170.315(g)(10) is inside the Base EHR definition (§170.102), so a tier without this
 * module is not CEHRT and cannot support MIPS / Promoting Interoperability attestation. That must
 * be conspicuously disclosed per §170.523(k)(1) — a marketing obligation this module cannot
 * discharge, only flag.
 *
 * `onc.cqmReporting` (`BACKLOG.md` N6, added 2026-08-26) is the same reasoning applied to
 * §170.315(c)(1)/(c)(3) — CQM capture and reporting is itself inside the Base EHR definition, so it
 * belongs in this indivisible module for the identical reason `onc.standardizedApi` does: a tier
 * that could gate away CQM capture couldn't be CEHRT, and MIPS reporting has no smaller "add-on"
 * version to sell separately.
 */
export const CERTIFIED_MODULE_CAPABILITIES = [
  'onc.standardizedApi',
  'onc.bulkExport',
  'onc.ccdaExchange',
  'onc.cqmReporting',
] as const;

export const FEATURES = [
  ...COMMERCIAL_FEATURES,
  ...NEVER_GATED,
  ...CERTIFIED_MODULE_CAPABILITIES,
] as const;
export type Feature = (typeof FEATURES)[number];

/** Which commercial features each tier includes. Only `COMMERCIAL_FEATURES` may appear here. */
const COMMERCIAL_FEATURES_BY_TIER: Record<Tier, readonly (typeof COMMERCIAL_FEATURES)[number][]> = {
  starter: [],
  growth: [
    'sso',
    'analytics',
    'dermatologyModule',
    'aestheticsModule',
    'physicalTherapyModule',
    'pediatricsModule',
    'gastroenterologyModule',
    'orthopedicsModule',
    'ophthalmologyModule',
    'obgynModule',
  ],
  enterprise: [
    'sso',
    'scim',
    'whiteLabel',
    'analytics',
    'dermatologyModule',
    'aestheticsModule',
    'physicalTherapyModule',
    'pediatricsModule',
    'gastroenterologyModule',
    'orthopedicsModule',
    'ophthalmologyModule',
    'obgynModule',
  ],
};

/**
 * Whether each tier licenses the certified module.
 *
 * **Every paid tier does** — settled by `emr-po` A9: "every ONC-certified capability available on
 * every paid tier with nothing gated." That is also the simplest posture to defend, and it retires
 * most of the regulatory exposure C10 was opened to investigate: there is no provider-facing
 * certified-API price differential to justify under §170.404's cost-recovery test, and no tier that
 * fails to be CEHRT and therefore cannot support MIPS attestation.
 *
 * Kept as a per-tier table rather than collapsed to a constant `true`, deliberately. The table is
 * where a future commercial decision would have to be written down, and a reviewer diffing it would
 * see a tier being un-certified as an explicit change requiring C10 — whereas deleting the table
 * would make that change invisible. `isCehrt` and the all-or-nothing tests read from here.
 */
const LICENSES_CERTIFIED_MODULE: Record<Tier, boolean> = {
  starter: true,
  growth: true,
  enterprise: true,
};

function includes(list: readonly string[], value: string): boolean {
  return list.includes(value);
}

/**
 * Does `tier` include `feature`?
 *
 * Fails closed for commercial features: an unrecognised tier gets nothing, rather than throwing or
 * defaulting open. A tier value arriving here unrecognised means corrupt data or a half-finished
 * migration, and neither should hand out paid features.
 *
 * Fails *open* for `NEVER_GATED`, deliberately and for a different reason: corrupt tier data must
 * never be the thing that denies a patient access to their own EHI.
 *
 * Resolve this at read time, every time — never cache the answer in a column. A flag set once at
 * upgrade and never re-checked survives a downgrade silently: the tier that granted it is gone, but
 * nothing un-sets the flag, so the org keeps a capability it is no longer paying for.
 */
export function isEntitled(tier: string, feature: Feature): boolean {
  if (includes(NEVER_GATED, feature)) return true;

  if (!isTier(tier)) return false;

  if (includes(CERTIFIED_MODULE_CAPABILITIES, feature)) {
    return LICENSES_CERTIFIED_MODULE[tier];
  }

  const commercial = feature as (typeof COMMERCIAL_FEATURES)[number];
  return COMMERCIAL_FEATURES_BY_TIER[tier].includes(commercial);
}

/**
 * Every certified capability available to `tier` — all of them, or none.
 *
 * The all-or-nothing return shape is the point: it makes partial enablement of the certified scope
 * unrepresentable, which is what §170.402(a)(3) requires.
 */
export function certifiedCapabilitiesFor(
  tier: string,
): readonly (typeof CERTIFIED_MODULE_CAPABILITIES)[number][] {
  if (!isTier(tier)) return [];
  return LICENSES_CERTIFIED_MODULE[tier] ? CERTIFIED_MODULE_CAPABILITIES : [];
}

/**
 * Is a tier CEHRT — usable by a practice attesting to MIPS / Promoting Interoperability?
 *
 * Exists so marketing copy and the in-app plan comparison can derive this rather than restate it,
 * since misrepresenting it is the exact fact pattern behind the eClinicalWorks, Practice Fusion,
 * Greenway, and NextGen False Claims Act settlements.
 */
export function isCehrt(tier: string): boolean {
  return isTier(tier) && LICENSES_CERTIFIED_MODULE[tier];
}

export function isTier(value: string): value is Tier {
  return includes(TIERS, value);
}

export function isFeature(value: string): value is Feature {
  return includes(FEATURES, value);
}
